import React from "react";
import { Link } from "react-router-dom";
import ProductPage from "../../components/ProductPages/ProductPage";
import {
  generateBreadcrumbStructuredData,
  generateWebPageStructuredData,
} from "../../components/SEO/StructuredData";

const DESCRIPTION =
  "Bowling tournament software for one weekend: online signups and payments before, scoring and side pots during, payouts and checks after.";

const STAGES = [
  {
    title: "Before: fill the squads and take payments",
    pain: "Phone calls and napkin waitlists leave you guessing who has a spot.",
    doHere:
      "Bowlers sign up online, join a waitlist when a squad is full, and pay with Pay now when you turn card payments on.",
    importLine:
      "League sheets and paper signup sheets: upload a photo or PDF, review the rows, and use them before the first squad.",
    links: [
      { to: "/bowling-tournament-signups", label: "Signups" },
      { to: "/bowling-tournament-signup-sheet-import", label: "Signup sheet import" },
      { to: "/bowling-tournament-card-payments", label: "Card payments" },
      { to: "/bowling-tournament-averages", label: "Averages" },
      { to: "/bowling-tournament-average-import", label: "Average import" },
    ],
  },
  {
    title: "During: post scores once",
    pain: "The same game ends up on three side-pot sheets while bowlers crowd the desk for standings.",
    doHere:
      "Enter each game once. Standings and side pots update from those scores. Brackets use the games you already posted.",
    importLine:
      "Recap sheets between squads: photograph the filled sheet and put the games on the right team.",
    links: [
      { to: "/bowling-tournament-scoring", label: "Scoring" },
      { to: "/bowling-tournament-recap-sheet-import", label: "Recap sheet import" },
      { to: "/bowling-bracket-software", label: "Brackets" },
    ],
  },
  {
    title: "After: lock payouts and print checks",
    pain: "Side-pot math by hand and bowlers asking why a check says $38.",
    doHere:
      "Lock prize funds and side pots on one payout table. Mark cash paid. Export checks from those same amounts.",
    links: [
      { to: "/bowling-tournament-payouts", label: "Payouts" },
      { to: "/bowling-tournament-check-exports", label: "Check exports" },
      { to: "/bowling-payout-calculator", label: "Payout calculator" },
    ],
  },
];

const BowlingTournamentSoftware = () => (
  <ProductPage
    title="Bowling tournament software | Bowling Tournament App"
    description={DESCRIPTION}
    keywords={[
      "bowling tournament software",
      "bowling tournament management",
      "bowling tournament signups scoring payouts",
    ]}
    canonicalUrl="/bowling-tournament-software"
    heading="One weekend on one board"
    lede="Before the tournament you fill squads and take payments. During it you post scores once. After it you lock payouts and print checks."
    analyticsLocation="product_software"
    showNextStep={false}
    structuredData={generateWebPageStructuredData({
      name: "Bowling tournament software",
      description: DESCRIPTION,
      url: "/bowling-tournament-software",
      dateModified: "2026-11-04",
    })}
    additionalStructuredData={[
      generateBreadcrumbStructuredData([
        { label: "Home", path: "/" },
        {
          label: "Bowling tournament software",
          path: "/bowling-tournament-software",
        },
      ]),
    ]}
  >
    <div className="space-y-12 max-w-3xl">
      {STAGES.map((stage) => (
        <section key={stage.title}>
          <h2 className="font-display font-bold tracking-tight text-2xl text-brand-charcoal">
            {stage.title}
          </h2>
          <div className="mt-3 h-1 w-12 bg-brand-gold" aria-hidden="true" />
          <p className="mt-4 text-brand-muted leading-relaxed">{stage.pain}</p>
          <p className="mt-3 text-brand-muted leading-relaxed">{stage.doHere}</p>
          {stage.importLine ? (
            <p className="mt-3 text-brand-muted leading-relaxed">
              {stage.importLine}
            </p>
          ) : null}
          <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm">
            {stage.links.map((link) => (
              <li key={link.to}>
                <Link to={link.to} className="underline text-brand-gold-ink">
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <p className="text-sm text-brand-muted">
        Free includes 5 tournaments per year.{" "}
        <Link to="/pricing" className="underline text-brand-gold-ink">
          Plans
        </Link>
      </p>
    </div>
  </ProductPage>
);

export default BowlingTournamentSoftware;
